import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonWithGAClickButton,
  ModalMediaCardBody,
  ModalMediaCardFooter,
  ModalMediaCardGallery,
  ModalMediaCardImage,
  ModalMediaCardStructure,
  MoreInfoCard,
  MoreInfoCardBody,
  MoreInfoCardGrid,
  MoreInfoCardImage,
  Typography,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { analyticsUtils } from 'utils/analytics-utils';
import { createElementKey } from 'utils';
import { CEILINGS_ELEMENT_BLOCK } from '../../ceilings-constants';
import { MoreInfoCardStructure } from './ceilings-more-info-types';

type CeilingsMoreInfoProps = {
  cardPerRow: number;
  className?: string;
  onConsultationSignupBtnClick: () => void;
  cardList: ModalMediaCardStructure<MoreInfoCardStructure>[];
};

export const CeilingsMoreInfo: React.FC<CeilingsMoreInfoProps> = ({
  cardList,
  cardPerRow,
  className,
  onConsultationSignupBtnClick,
}) => {
  const { t } = useTranslation();
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(undefined);

  const promotions = useMemo(
    () =>
      cardList.map(({ title }: MoreInfoCardStructure, index: number) => ({
        name: CEILINGS_ELEMENT_BLOCK,
        creative: t(title),
        position: (index + 1).toString(),
      })),
    [cardList, t]
  );

  usePromoViewEvent(cardRefs, CEILINGS_ELEMENT_BLOCK, promotions);

  /**
   * Обработчик закрытия модального окна
   */
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleCardChange = useCallback((index: number) => {
    setOpenCardIndex(index);
  }, []);

  /**
   * Обработчик нажатия на карточку со стилями
   */
  const handleCardClick = useCallback(
    (index: number) => () => {
      setOpenCardIndex(index);
      setIsModalOpen(true);
      analyticsUtils.promoClickEvent(CEILINGS_ELEMENT_BLOCK, promotions[index]);
    },
    [promotions, setIsModalOpen, setOpenCardIndex]
  );

  const handleFindOutPriceBtnClick = useCallback(() => {
    setIsModalOpen(false);
    setOpenCardIndex(0);
    onConsultationSignupBtnClick();
  }, [onConsultationSignupBtnClick]);

  return (
    <>
      <MoreInfoCardGrid cardNumber={cardList.length} cardPerRow={cardPerRow} className={className}>
        {cardList.map(({ imgSrc, title }, index) => (
          <MoreInfoCard
            data-position={index}
            key={createElementKey('mounting-type', index)}
            onClick={handleCardClick(index)}
            ref={(el: HTMLDivElement | null) => {
              if (el && cardRefs) cardRefs.current[index] = el;
            }}>
            <MoreInfoCardImage src={imgSrc} alt={t(title) || ''} />
            <MoreInfoCardBody title={t(title)} />
          </MoreInfoCard>
        ))}
      </MoreInfoCardGrid>

      <ModalMediaCardGallery
        cardList={cardList}
        elementBlock={CEILINGS_ELEMENT_BLOCK}
        isOpen={isModalOpen}
        openCardIndex={openCardIndex}
        onCardChange={handleCardChange}
        onClose={handleCloseModal}
        renderContent={({ description, modalImgSrc, title }) => (
          <>
            <ModalMediaCardBody title={t(title) || ''}>
              <Typography adaptive={false} variant="m" size="body_long">
                {t(description)}
              </Typography>

              <ModalMediaCardFooter>
                <ButtonWithGAClickButton
                  elementBlock={CEILINGS_ELEMENT_BLOCK}
                  elementName={t(title)}
                  elementText={t('types-section.button')}
                  onClick={handleFindOutPriceBtnClick}
                  wide={false}>
                  {t('types-section.button')}
                </ButtonWithGAClickButton>
              </ModalMediaCardFooter>
            </ModalMediaCardBody>
            <ModalMediaCardImage alt={title} src={modalImgSrc} title={title} />
          </>
        )}
      />
    </>
  );
};
